import React from "react";
import { Alert, Box, Button, Chip, Sheet, Typography } from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminEmailAPI } from "../../lib/apiAdmin";
import { getApiErrorMessage } from "../../lib/apiError";

/**
 * Connect the mailbox that outbound email is sent from.
 *
 * Without a connected account every send fails with "Connect your mailbox
 * before sending" — so this is the screen that error was pointing at.
 */
export const MailboxConnection: React.FC<{ compact?: boolean }> = ({
  compact,
}) => {
  const queryClient = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ["admin", "emailAccounts"],
    queryFn: () => AdminEmailAPI.listAccounts(),
  });

  const connectMutation = useMutation({
    mutationFn: (provider: "gmail" | "outlook") =>
      AdminEmailAPI.connectUrl(provider, window.location.pathname),
    onSuccess: (url) => {
      // Full navigation, not a popup: the provider's consent screen refuses to
      // render in an iframe and popups are commonly blocked.
      window.location.href = url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => AdminEmailAPI.disconnect(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "emailAccounts"] }),
  });

  const accounts = accountsQuery.data ?? [];

  const sender = accounts.find(
    (account) => account.is_active && account.can_send,
  );
  if (compact && sender)
    return (
      <Alert color="success" variant="soft" size="sm">
        Sending from {sender.email_address}
        {sender.source === "profile" ? " · Connected in Profile Settings" : ""}
      </Alert>
    );
  if (accountsQuery.isLoading)
    return (
      <Typography level="body-sm">
        Checking your connected mailboxes…
      </Typography>
    );

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {accountsQuery.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button size="sm" onClick={() => accountsQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Could not load your connected mailboxes.
        </Alert>
      )}
      {(connectMutation.error || disconnectMutation.error) && (
        <Alert color="danger">
          {getApiErrorMessage(
            connectMutation.error || disconnectMutation.error,
            "Could not update your email connection.",
          )}
        </Alert>
      )}
      {new URLSearchParams(window.location.search).has("email_error") && (
        <Alert color="warning">
          The email connection could not be completed. Please try again.
        </Alert>
      )}
      {accounts.map((account) => (
        <Sheet
          key={account.id}
          variant="soft"
          sx={{
            p: 1.25,
            borderRadius: "sm",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-sm">{account.email_address}</Typography>
            {account.source === "profile" && (
              <Typography level="body-xs">
                Shared with Profile Settings and Inbox
              </Typography>
            )}
            {account.last_sync_error && (
              <Typography level="body-xs" color="danger">
                {account.last_sync_error}
              </Typography>
            )}
          </Box>
          <Chip
            size="sm"
            variant="soft"
            color={account.can_send ? "success" : "warning"}
          >
            {account.can_send ? "Can send" : "Reconnect needed"}
          </Chip>
          {account.source !== "profile" && (
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => disconnectMutation.mutate(account.id)}
            >
              Disconnect
            </Button>
          )}
        </Sheet>
      ))}

      {accounts.length === 0 && !accountsQuery.isError && (
        <Alert color="warning" variant="soft" size="sm">
          Connect Gmail or Outlook once to use it here and in Profile Settings.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          size="sm"
          variant="outlined"
          loading={connectMutation.isPending}
          onClick={() => connectMutation.mutate("gmail")}
        >
          Connect Gmail
        </Button>
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          loading={connectMutation.isPending}
          onClick={() => connectMutation.mutate("outlook")}
        >
          Connect Outlook
        </Button>
      </Box>

      {/* Disconnecting forgets our tokens but does not revoke the grant with
          the provider; that has to be done in the provider's own settings. */}
      {accounts.length > 0 && (
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Disconnecting removes our copy of the access. Revoke it fully in your
          Google or Microsoft account settings.
        </Typography>
      )}
    </Box>
  );
};

export default MailboxConnection;
